import React from 'react';

type ActionType = 'register' | 'unregister';
type Action = { type: ActionType };
type Dispatch = (action: Action) => void;

const ModalCountContext = React.createContext(0);
const ModalRegistryDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

function modalRegistryReducer(count: number, action: Action) {
  switch (action.type) {
    case 'register': {
      return count + 1;
    }
    case 'unregister': {
      return Math.max(0, count - 1);
    }
  }
}

function ModalRegistryProvider({ children }: { children: React.ReactNode }) {
  const [count, dispatch] = React.useReducer(modalRegistryReducer, 0);
  return (
    <ModalRegistryDispatchContext.Provider value={dispatch}>
      <ModalCountContext.Provider value={count}>
        {children}
      </ModalCountContext.Provider>
    </ModalRegistryDispatchContext.Provider>
  );
}

// Counts a modal as open for as long as isOpen stays true. Call it above any
// `if (!isOpen) return null` so the hook order stays stable. Modals rendered
// outside the provider (stories) are simply never counted.
function useModalRegistration(isOpen: boolean) {
  const dispatch = React.useContext(ModalRegistryDispatchContext);
  React.useEffect(() => {
    if (!isOpen || !dispatch) return;
    dispatch({ type: 'register' });
    return () => dispatch({ type: 'unregister' });
  }, [isOpen, dispatch]);
}

// True while any modal is open, for global shortcuts that shouldn't fire
// underneath one.
function useAnyModalOpen() {
  return React.useContext(ModalCountContext) > 0;
}

export { ModalRegistryProvider, useModalRegistration, useAnyModalOpen };
